import classNames from "classnames";
import { useFormikContext } from "formik";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CaretDown } from "components/icons/CaretDown";
import {
  TAX_FIELD_TYPE,
  TaxSetupQuestion as TaxSetupQuestionType,
  TaxSetupValues,
} from "v2/constants/taxSetupQuestions";
import { TaxSetupSectionId } from "v2/constants/taxSetupSections";
import { collectVisibleQuestions } from "v2/utils/collectVisibleQuestions";
import { groupQuestionsBySection } from "v2/utils/groupQuestionsBySection";
import { isQuestionAnswered } from "v2/utils/isQuestionAnswered";

type TaxSetupSectionsProps = {
  questions: TaxSetupQuestionType[];

  namePrefix: string;

  renderQuestion: (question: TaxSetupQuestionType) => ReactNode;
};

type CountedSection = {
  id: TaxSetupSectionId;
  title: string;
  questions: TaxSetupQuestionType[];
  answered: number;
  total: number;
  isComplete: boolean;
};

const NO_SECTION_OPEN = "";

export const TaxSetupSections = ({
  questions,
  namePrefix,
  renderQuestion,
}: TaxSetupSectionsProps) => {
  const { values } = useFormikContext<TaxSetupValues>();

  const sections: CountedSection[] = groupQuestionsBySection(questions).map(
    (section) => {
      const counted = collectVisibleQuestions(
        section.questions,
        values,
        namePrefix
      ).filter((question) => question.fieldType !== TAX_FIELD_TYPE.ENTITY_LIST);

      const answered = counted.filter((question) =>
        isQuestionAnswered(values[`${namePrefix}${question.id}`])
      ).length;

      return {
        ...section,
        answered,
        total: counted.length,
        isComplete: answered === counted.length,
      };
    }
  );

  const [firstSection] = sections;
  const firstIncomplete = sections.find((section) => !section.isComplete);

  const [openSection, setOpenSection] = useState<string>(
    firstIncomplete?.id ?? firstSection?.id ?? NO_SECTION_OPEN
  );

  const isOpenSectionComplete = sections.some(
    (section) => section.id === openSection && section.isComplete
  );
  const nextIncompleteId = firstIncomplete?.id;

  const fillingSection = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpenSectionComplete) {
      fillingSection.current = openSection;
      return;
    }

    if (fillingSection.current !== openSection || !nextIncompleteId) return;

    fillingSection.current = null;
    setOpenSection(nextIncompleteId);
  }, [isOpenSectionComplete, nextIncompleteId, openSection]);

  return (
    <Accordion.Root
      type="single"
      collapsible
      value={openSection}
      onValueChange={setOpenSection}
      className="t-flex t-flex-col t-gap-6"
    >
      {sections.map((section) => {
        const isOpen = section.id === openSection;

        const caretClassName = classNames(
          "t-flex t-text-neutral-40 t-transition t-duration-300 t-ease-in-out",
          isOpen ? "t-rotate-0" : "-t-rotate-90"
        );

        return (
          <Accordion.RawItem
            key={section.id}
            value={section.id}
            className="t-flex t-flex-col t-gap-4"
          >
            <Accordion.RawTrigger asChild>
              <button
                type="button"
                className="all:unset t-flex t-w-full t-select-none t-items-center t-gap-2"
              >
                <span className={caretClassName}>
                  <CaretDown width="16" height="16" />
                </span>

                <span className="t-text-subtext t-text-text-100">
                  {section.title}
                </span>

                <Tag
                  icon={false}
                  rounded
                  size="small"
                  tagType={section.isComplete ? "green" : "gray"}
                >
                  {section.answered}/{section.total}
                </Tag>
              </button>
            </Accordion.RawTrigger>

            <Accordion.RawContent className="t-overflow-hidden data-state-closed:t-animate-accordian-slide-up data-state-open:t-animate-accordian-slide-down">
              <div className="t-flex t-flex-col t-gap-4 t-pl-6">
                {section.questions.map((question) => (
                  <div key={question.id}>{renderQuestion(question)}</div>
                ))}
              </div>
            </Accordion.RawContent>
          </Accordion.RawItem>
        );
      })}
    </Accordion.Root>
  );
};
